import { useState, useEffect } from "react";
import { FolderOpen, FileText, Download, Trash2, Loader2, Eye, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/pages/Residents";
import { Button } from "@/components/ui/button";

function fmtSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso) {
  try { return new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }); }
  catch (_) { return iso; }
}

export default function DokumenSaya() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/documents"); setDocs(r.data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openDoc = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await api.get(`/documents/${doc.id}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  const downloadDoc = async (doc) => {
    setBusyId(doc.id);
    try {
      const r = await api.get(`/documents/${doc.id}/file`, { responseType: "blob", params: { dl: 1 } });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename || "document";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  const removeDoc = async (doc) => {
    setBusyId(doc.id);
    try { await api.delete(`/documents/${doc.id}`); toast.success("Dokumen dihapus"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusyId(""); }
  };

  const isImage = (m) => (m || "").startsWith("image/");

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <PageHeader icon={FolderOpen} title="Dokumen Saya" subtitle="Arsip dokumen hasil pindai (tersimpan aman di penyimpanan Telegram privat)" />

      {loading ? (
        <div className="p-16 grid place-items-center"><Loader2 className="animate-spin text-[#064E3B]" size={28} /></div>
      ) : docs.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-16 text-center">
          <div className="h-14 w-14 rounded-2xl bg-emerald-50 grid place-items-center mx-auto mb-4"><FolderOpen className="text-[#064E3B]" /></div>
          <p className="text-slate-500">Belum ada dokumen. Gunakan Smart Scanner untuk memindai.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="documents-list">
          {docs.map((doc) => (
            <div key={doc.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`doc-card-${doc.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-emerald-50 grid place-items-center shrink-0">
                  {isImage(doc.mime_type) ? <ImageIcon className="text-[#064E3B]" size={20} /> : <FileText className="text-[#064E3B]" size={20} />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 truncate">{doc.original_filename}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{fmtDate(doc.created_at)}</div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Tag>{doc.paper_size} {doc.orientation}</Tag>
                    <Tag>{doc.pages} hal</Tag>
                    <Tag>{fmtSize(doc.file_size)}</Tag>
                    {doc.document_type && doc.document_type !== "umum" && <Tag>{doc.document_type}</Tag>}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                <Button size="sm" variant="outline" onClick={() => openDoc(doc)} disabled={busyId === doc.id} className="rounded-lg" data-testid={`doc-open-${doc.id}`}><Eye size={15} className="mr-1.5" /> Buka</Button>
                <Button size="sm" variant="outline" onClick={() => downloadDoc(doc)} disabled={busyId === doc.id} className="rounded-lg" data-testid={`doc-download-${doc.id}`}><Download size={15} className="mr-1.5" /> Unduh</Button>
                <Button size="sm" variant="outline" onClick={() => removeDoc(doc)} disabled={busyId === doc.id} className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 ml-auto" data-testid={`doc-delete-${doc.id}`}><Trash2 size={15} /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Tag({ children }) {
  return <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">{children}</span>;
}
